
import Vue from 'vue';
import { getBrandConfig } from '@/config/brands';
import { Category } from '@/config/types';
import { getLauncherUrl } from '@/services/gameLauncher';

interface GameFile {
  name: string;
  type: string;
  action: string;
  actionType: string;
  url?: string;
  gameId?: string;
  launchProvider?: string;
}

interface SelectedGame {
  name: string;
  gameType?: string;
  banner?: string;
  subtitle?: string;
  gameFiles?: GameFile[];
}

export default Vue.extend({
  name: 'MainBanner',
  props: {
    selectedGame: {
      type: Object as () => SelectedGame | null,
      default: null
    },
    selectedCategory: {
      type: String,
      default: null
    },
    currentBrandId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      currentBannerIndex: 0,
      rotationInterval: null as any,
      isLaunching: false,
    };
  },
  computed: {
    brandConfig() {
      return getBrandConfig(this.currentBrandId);
    },
    categoryBanners(): Record<string, string> {
      const banners: Record<string, string> = {};
      this.brandConfig.categories.forEach((cat: Category) => {
        banners[cat.name] = cat.banner;
      });
      return banners;
    },
    defaultBanners(): string[] {
      const configured = this.brandConfig.defaultBanners;
      if (configured && configured.length > 0) return configured;
      return this.brandConfig.categories
        .filter((cat: Category) => !cat.disabled)
        .map((cat: Category) => cat.banner);
    },
    categoryTitles(): string[] {
      return this.brandConfig.categories
        .filter((cat: Category) => !cat.disabled)
        .map((cat: Category) => cat.name);
    },
    bannerImage(): string {
      // 1. If a specific game is selected, use the game's banner
      if (this.selectedGame && this.selectedGame.banner) {
        try {
          return require(`@/assets/${this.selectedGame.banner}`);
        } catch (e) {
          const bannerPath = this.selectedGame.banner;
          if (bannerPath.endsWith('.png')) {
            try {
              return require(`@/assets/${bannerPath.replace('.png', '.jpg')}`);
            } catch (e2) {
            }
          } else if (bannerPath.endsWith('.jpg')) {
            try {
              return require(`@/assets/${bannerPath.replace('.jpg', '.png')}`);
            } catch (e2) {
              console.warn(`Banner not found: ${bannerPath}`);
            }
          }
        }
      }
      
      // 2. If a category is selected, use the category's banner
      if (this.selectedCategory && this.categoryBanners[this.selectedCategory]) {
        try {
          return require(`@/assets/${this.categoryBanners[this.selectedCategory]}`);
        } catch (e) {
          console.warn(`Category banner not found: ${this.categoryBanners[this.selectedCategory]}`);
        }
      }
      
      // 3. Default state - rotating banners
      try {
        return require(`@/assets/${this.defaultBanners[this.currentBannerIndex]}`);
      } catch (e) {
        console.warn(`Rotating banner not found: ${this.defaultBanners[this.currentBannerIndex]}`);
        return require('@/assets/banners/mainBanner.png');
      }
    },
    gameTitle(): string {
      // 1. If a specific game is selected, use the game's name
      if (this.selectedGame && this.selectedGame.name) {
        return this.selectedGame.name;
      }
      
      // 2. If a category is selected, use the category's name
      if (this.selectedCategory) {
        return this.selectedCategory;
      }
      
      // 3. Default state: title rotation follows the banners
      const title = this.categoryTitles[this.currentBannerIndex];
      return title;
    }
  },
  methods: {
    async handleFileAction(file: GameFile) {
      if (file.actionType === 'disabled') return;

      if (file.actionType =='api-launch' && file.gameId) {
        this.isLaunching = true;
        const win = window.open('', '_blank');
        try {
          const url = await getLauncherUrl(file.gameId, file.launchProvider);
          if (win) {
            win.location.href = url;
          } else {
            window.open(url, '_blank');
          }
        } catch (e) {
          win?.close();
          throw e;
        } finally {
          this.isLaunching = false;
        }
        return;
      }

      if (file.actionType === 'button' && file.url) {
        window.open(file.url, '_blank');
      } else if (file.actionType === 'download' && file.url) {
        // Resolve against CDN base URL so assets in public/downloads/ are served
        // from S3/CloudFront, not nginx. Handles URLs with or without leading slash.
        const cdnBase = (process.env.VUE_APP_CDN_URL || '/').replace(/\/$/, '');
        const relativePath = file.url.startsWith('/') ? file.url.slice(1) : file.url;
        const link = document.createElement('a');
        link.href = `${cdnBase}/${relativePath}`;
        link.download = '';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },
    
    goBack() {
      this.$emit('go-back');
    },
    
    goToSlide(index: number) {
      this.currentBannerIndex = index;
      this.resetRotationTimer();
    },
    
    resetRotationTimer() {
      this.stopBannerRotation();
      this.startBannerRotation();
    },
    
    startBannerRotation() {
      
      // Only start rotation if no game or category is selected
      if (!this.selectedGame && !this.selectedCategory) {
        this.rotationInterval = setInterval(() => {
          this.currentBannerIndex = (this.currentBannerIndex + 1) % this.defaultBanners.length;
        }, 5000);
      } else {
        this.currentBannerIndex = 0;
      }
    },
    
    stopBannerRotation() {
      if (this.rotationInterval) {
        clearInterval(this.rotationInterval);
        this.rotationInterval = null;
      }
    }
  },
  
  mounted() {
    this.startBannerRotation();
  },
  
  beforeDestroy() {
    this.stopBannerRotation();
  },
  
  watch: {
    selectedGame() {
      this.stopBannerRotation();
      this.startBannerRotation();
    },
    
    selectedCategory() {
      this.stopBannerRotation();
      this.startBannerRotation();
    }
  }
});
